import React, { useState, useEffect } from "react";
import { FaCaretDown, FaCaretRight } from "react-icons/fa";
import DashboardLayout from "../DashboardLayout";
import {
  addBulkRolePermissions,
  addSingleRolePermission,
  fetchRolePermissionsByModules,
  removeBulkRolePermissions,
  removeRolePermission,
} from "../../services/axios/apiManager";
import { ToggleSwitch } from "./switches";
import { toast } from "sonner";
import { handleErrors } from "../../services/dataManager";
import { useAppContext } from "../../services/context";

const AddEditRolePermissions = ({ navigate }) => {
  const { state } = useAppContext();
  const { permissions } = state;
  const [roleId, setRoleId] = useState(null);
  const [permissionsByModule, setPermissionsByModules] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [granted, setGranted] = useState([]);

  const extractPermissions = () => {
    const perms = permissions.filter((f) => f.module === "Role_Permissions");
    if (perms) {
      setGranted(perms);
    }
  };

  // // // fetch all Data on component load and reset on unmout
  const getPermsData = async (roleId) => {
    const res = await fetchRolePermissionsByModules(roleId);
    if (res?.data?.success) {
      setPermissionsByModules(res.data.data);
    } else {
      handleErrors(res, navigate);
    }
  };

  useEffect(() => {
    extractPermissions();
    const roleId = localStorage.getItem("roleId");
    if (roleId) {
      setRoleId(roleId);
      getPermsData(roleId);
    } else {
      toast("Can't find a role.", { duration: 2000 });
      navigate("/rolePermissions");
    }
    return () => {
      setRoleId(null);
      localStorage.removeItem("roleId");
    };
  }, []);

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  ///////////////////////////////////////////////// Handle main category toggle
  const handleToggleCategory = async (moduleId, module_permissions = []) => {
    const isAdded = await changeOnLocal(moduleId, module_permissions);
    const ids = module_permissions?.map((perm) => perm.id);
    const data = {
      role_id: roleId,
      permissionIds: ids,
    };
    try {
      if (isAdded === true) {
        await handleRemoveBulkPermissions(data);
      } else if (isAdded === false) {
        await handleAddBulkPermissions(data);
      }
      await getPermsData(roleId);
    } catch (error) {
      console.error("Error during bulk permission update:", error);
      // Revert local state if API call fails
      await getPermsData(roleId);
    }
  };

  // Change category toggle button locally
  const changeOnLocal = (moduleId, module_permissions) => {
    const isOn = module_permissions?.every((perm) => perm.permissionToRole);
    if (isOn && !granted?.some((perm) => perm.type === "remove-bulk")) {
      toast.error(
        "You don't have neccessary permissions to do this operation.",
        { duration: 3000 }
      );
      return null;
    }
    if (!isOn && !granted?.some((perm) => perm.type === "add-bulk")) {
      toast.error(
        "You don't have neccessary permissions to do this operation.",
        { duration: 3000 }
      );
      return null;
    }
    const updatedPermissions = permissionsByModule.map((module) =>
      module.id === moduleId
        ? {
            ...module,
            module_permissions: module_permissions?.map((perm) => ({
              ...perm,
              permissionToRole: !isOn,
            })),
          }
        : module
    );

    setPermissionsByModules(updatedPermissions);
    return isOn;
  };

  // Adding bulk permissions
  const handleAddBulkPermissions = async (data = {}) => {
    const res = await addBulkRolePermissions(data);
    if (res?.data?.success) {
      toast("Module Permissions have been granted.", { duration: 2000 });
    } else {
      toast("Error upon granting module permissions.", { duration: 2000 });
    }
  };

  // Removing bulk permissions
  const handleRemoveBulkPermissions = async (data = {}) => {
    const res = await removeBulkRolePermissions(data);
    if (res?.data?.success) {
      toast("Module Permissions have been revoked.", { duration: 2000 });
    } else {
      toast("Error upon removing module permissions.", { duration: 2000 });
    }
  };

  ///////////////////////////////////////// Toggling single permission
  const handleTogglePermission = async (mid, pid, currentState) => {
    if (
      currentState &&
      !granted?.some((perm) => perm.type === "remove-single")
    ) {
      toast.error(
        "You don't have neccessary permissions to do this operation.",
        { duration: 3000 }
      );
      return;
    }
    if (!currentState && !granted?.some((perm) => perm.type === "add-single")) {
      toast.error(
        "You don't have neccessary permissions to do this operation.",
        { duration: 3000 }
      );
      return;
    }

    await changePermissionLocally(mid, pid);
    const data = {
      role_id: roleId || "",
      permission_id: pid || "",
    };
    try {
      if (currentState) {
        await handelRemoveSinglePermission(data);
      } else {
        await handelAddSinglePermission(data);
      }
      await getPermsData(roleId);
    } catch (error) {
      await getPermsData(roleId);
    }
  };

  // // // Change single permission locally
  const changePermissionLocally = async (mid, pid) => {
    const updatedPermissions = permissionsByModule.map((module) =>
      module.id === mid
        ? {
            ...module,
            module_permissions: module.module_permissions.map((perm) =>
              perm.id === pid
                ? { ...perm, permissionToRole: !perm.permissionToRole }
                : perm
            ),
          }
        : module
    );
    setPermissionsByModules(updatedPermissions);
  };

  // // // Add single permission on server
  const handelAddSinglePermission = async (data = {}) => {
    const res = await addSingleRolePermission(data);
    if (res?.data?.success) {
      toast.success("Permission granted", { duration: 2000 });
    } else {
      toast("Error upon granting permission.", { duration: 2000 });
    }
  };

  // // // Remove single permission from server
  const handelRemoveSinglePermission = async (data = {}) => {
    const res = await removeRolePermission(data);
    if (res?.data?.success) {
      toast.success("Permission revoked!", { duration: 2000 });
    } else {
      toast("Error upon revoking permission.", { duration: 2000 });
    }
  };

  return (
    <DashboardLayout>
      <div
        className="pl-[35px] pr-[35px] text-light container"
        style={{
          paddingLeft: "35px",
          paddingRight: "35px",
          paddingTop: "56px",
        }}
      >
        <div className="row">
          <div className="col-12 mb-3">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <div className="page-title-right">
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <a>System Settings</a>
                  </li>
                  <li className="breadcrumb-item active text-light">
                    Role Base Permission
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div
          className="container py-4 bg-dark rounded"
          style={{ color: "white", minHeight: "max-content" }}
        >
          <div className="row">
            <div className="col-lg-12">
              <div className="table-responsive">
                <table className="table table-hover table-striped align-middle table-borderless table-nowrap">
                  <thead className="table-dark">
                    <tr>
                      <th style={{ width: "60%" }}>Category</th>
                      <th style={{ width: "20%" }}>Status</th>
                      <th style={{ width: "10%" }}>permissions</th>
                      <th style={{ width: "10%" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="table-dark">
                    {permissionsByModule?.map(
                      ({ module_permissions, ...module }) => (
                        <React.Fragment key={module.id}>
                          <tr className="table-dark">
                            <td>
                              <div
                                className="row"
                                style={{
                                  cursor: "pointer",
                                }}
                                onClick={() => toggleCategory(module.name)}
                              >
                                <div className="  d-flex align-items-center">
                                  {expandedCategories[module.name] ? (
                                    <FaCaretDown className="mx-2" />
                                  ) : (
                                    <FaCaretRight className="mx-2" />
                                  )}
                                  {module.name}
                                </div>
                              </div>
                            </td>
                            <td
                              className={`${module.status ? "text-success" : "text-danger"}`}
                            >
                              {module.status === 1 ? "Active" : "Inactive"}
                            </td>
                            <td>
                              {module_permissions?.reduce(
                                (acc, val) =>
                                  acc + (val?.permissionToRole ? 1 : 0),
                                0
                              ) || 0}
                            </td>
                            <td>
                              <div className="d-flex justify-content-between align-items-center">
                                <ToggleSwitch
                                  isOn={module_permissions?.every(
                                    (mp) => mp?.permissionToRole
                                  )}
                                  color={"#4caf50"}
                                  onClick={() => {
                                    handleToggleCategory(
                                      module.id,
                                      module_permissions
                                    );
                                  }}
                                />
                              </div>
                            </td>
                          </tr>
                          {expandedCategories[module.name] &&
                            module_permissions?.map((mp) => (
                              <tr key={mp.id} className=" mb-2 mt-2">
                                <td style={{ paddingLeft: "30px" }}>
                                  {mp.description}
                                </td>
                                <td>{""}</td>
                                <td>
                                  <span
                                    className={`badge ${mp.status === "Active" ? "bg-success" : "bg-danger"}`}
                                  >
                                    {mp.status}
                                  </span>
                                </td>
                                <td>
                                  <ToggleSwitch
                                    isOn={mp?.permissionToRole || false}
                                    color={"#06cfe0"}
                                    onClick={() => {
                                      handleTogglePermission(
                                        module.id,
                                        mp.id,
                                        mp.permissionToRole || false
                                      );
                                    }}
                                  />
                                </td>
                              </tr>
                            ))}
                        </React.Fragment>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddEditRolePermissions;
